import { useState } from 'react'
import { useDebounce } from 'react-use'

import { useAppDispatch, useAppSelector } from '../../store'
import { finishLoading, startLoading } from '../../store/modules'
import { CircularProgress } from '../shared/NarusMaterial'

export function GlobalLoadingIndicator (): JSX.Element | null {
  let queue = useAppSelector(state => state.app.loadingQueue)

  let isVisible = queue.length > 0
  let [ debouncedIsVisible, setDebouncedIsVisible ] = useState(isVisible)

  useDebounce(() => {
    setDebouncedIsVisible(queue.length > 0)
  }, isVisible ? 0 : 1000, [ queue.length ])

  if (!debouncedIsVisible) {
    return null
  }

  let descriptions = queue.map(item => item.description)
    .filter(Boolean)
    .sort((a, b) => (a as string).localeCompare(b as string))
    .filter((v, i, a) => a.indexOf(v) === i)

  let title = `Working on ${queue.length} ${queue.length > 1 ? 'things' : 'thing'}: ${descriptions.join(', ')}`

  if (descriptions.length !== queue.length) {
    title = `${title} (and ${queue.length - descriptions.length} more)`
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '0',
        left: '0',
        padding: '1rem',
        zIndex: '100000',
        transform: 'scale(1.175)',
        transformOrigin: 'bottom left'
      }}
      title={title}
    >
      <CircularProgress
        className='tiny'
        color='blue'
        style={{
          pointerEvents: 'none'
        }}
      />
    </div>
  )
}

export function useGlobalLoadingIndicator (id: string): {
  startLoading: (descriptions: string) => void
  finishLoading: () => void
} {
  const dispatch = useAppDispatch()

  function _startLoading (description: string): void {
    dispatch(startLoading({ id, description }))
  }

  function _finishLoading (): void {
    dispatch(finishLoading(id))
  }

  return { startLoading: _startLoading, finishLoading: _finishLoading }
}
